import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Tabs, Tab, Form, Alert, Button, Breadcrumb } from "react-bootstrap";
import Title from "../../common/Title";
import { TbChartBar, TbArrowLeft, TbAlertCircle } from "react-icons/tb";
import { canAccessSuperUserAnalytics } from "../../../utils/authUtils";
import RunbookExecutionsTab from "../RunbookExecutionsTab";
import ApiPerformanceTab from "../ApiPerformanceTab";
import TrendsTab from "../TrendsTab";

function ClientAnalyticsView({ user }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [dateRange, setDateRange] = useState("oneMonth");
  const [activeTab, setActiveTab] = useState("runbooks");

  // Get client name from navigation state, or use clientId as fallback
  const clientName = location.state?.clientName || clientId;

  // Check authorization
  if (!canAccessSuperUserAnalytics(user)) {
    return (
      <div>
        <Title text="Client Analytics" icon={<TbChartBar className="mb-1" />} />
        <Alert variant="danger">
          <Alert.Heading className="d-flex align-items-center">
            <TbAlertCircle className="me-2" size={24} />
            Access Denied
          </Alert.Heading>
          <p className="mb-0">
            This page requires Super User or Product Team privileges.
            Please contact your administrator if you believe you should have access.
          </p>
        </Alert>
      </div>
    );
  }

  const handleBackToAllClients = () => {
    navigate("/analytics/all-clients");
  };

  return (
    <div>
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item onClick={handleBackToAllClients} style={{ cursor: "pointer" }}>
          All Clients Analytics
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{clientName}</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Title text={`Analytics - ${clientName}`} icon={<TbChartBar className="mb-1" />} />
        <Button variant="outline-secondary" onClick={handleBackToAllClients}>
          <TbArrowLeft className="me-2" />
          Back to All Clients
        </Button>
      </div>

      <Alert variant="info" className="mb-3">
        <small>
          Viewing analytics for: <strong>{clientName}</strong> (Client ID: <code>{clientId}</code>)
        </small>
      </Alert>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <Form.Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="24hours">Last 24 Hours</option>
              <option value="lastWeek">Last Week</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="oneMonth">Last 30 Days</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="ytd">Year to Date</option>
              <option value="1year">Last Year</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Tabs
        id="client-analytics-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="runbooks" title="Runbook Executions">
          <RunbookExecutionsTab
            user={user}
            dateRange={dateRange}
            clientId={clientId}
          />
        </Tab>
        <Tab eventKey="api" title="API Performance">
          <ApiPerformanceTab
            user={user}
            dateRange={dateRange}
            clientId={clientId}
          />
        </Tab>
        <Tab eventKey="trends" title="Trends Over Time">
          <TrendsTab
            user={user}
            dateRange={dateRange}
            clientId={clientId}
          />
        </Tab>
      </Tabs>
    </div>
  );
}

export default ClientAnalyticsView;
